import { clamp } from '../core/math';

/**
 * Optional tyre temperature and wear.
 *
 * Off by default, and deliberately so: it makes grip a function of *when* in the
 * lap you are, which breaks the standing guarantee that every mode and every
 * attempt shares one ideal lap and one set of braking points. With it enabled
 * the reference lap becomes an approximation rather than a contract, and the
 * results screen says as much.
 *
 * The model is simple on purpose. Cold tyres give up grip, working range gives a
 * little back, and sustained sliding overheats them and takes it away again —
 * which punishes exactly the abuse the braking mechanic already discourages.
 */

export interface TyreState {
  /** 0 = stone cold, 1 = fully in the window, >1 = overheating. */
  temp: number;
  /** 0 = new, 1 = finished. Accumulates with sliding, not with distance. */
  wear: number;
}

export const COLD_GRIP = 0.9;
export const PEAK_GRIP = 1.03;
export const OVERHEAT_GRIP = 0.87;
/** Grip lost across a full life of wear. */
export const WEAR_GRIP_LOSS = 0.08;

// Same equilibrium temperatures as before (the ratios are unchanged), but
// reached over roughly 2.5x longer — a lap or more of gentle work rather than
// one second, closer to how long a real tyre actually takes to come in.
// Scaling WARM_RATE, SLIDE_HEAT and COOL_RATE by the same factor changes only
// the time constant, never the equilibrium `heating / COOL_RATE - 1`
// converges to, which is what the "warms up, then overheats when abused" test
// depends on — see that test for the tick counts this pace assumes.
const RATE_SCALE = 0.4;
const WARM_RATE = 0.55 * RATE_SCALE;
const COOL_RATE = 0.12 * RATE_SCALE;
const SLIDE_HEAT = 0.9 * RATE_SCALE;
// Wear does not share the warm/cool equilibrium's constraint, so it can move
// on its own: continuous full-slide abuse destroyed a set in under a minute
// and a half, which undersold how much margin a tyre actually has.
const WEAR_RATE = 0.0016;

export function createTyres(): TyreState {
  return { temp: 0, wear: 0 };
}

/**
 * @param slip Magnitude of tyre slip, roughly how hard the kart is working.
 * @param load 0..1 how close to the grip limit the axles are.
 */
export function stepTyres(t: TyreState, slip: number, load: number, dt: number): void {
  const working = clamp(load, 0, 1);
  const sliding = clamp(slip / 6, 0, 1.6);

  // Energy in from working the tyre, plus extra from actually sliding it.
  const heating = working * WARM_RATE + sliding * SLIDE_HEAT;
  const cooling = COOL_RATE * (1 + t.temp);
  t.temp = Math.max(0, t.temp + (heating - cooling) * dt);

  // Only sliding wears them; clean laps barely touch the tyre.
  t.wear = clamp(t.wear + sliding * sliding * WEAR_RATE * dt, 0, 1);
}

/** Multiplier on mu from the current tyre state. */
export function tyreGrip(t: TyreState): number {
  let g: number;
  if (t.temp < 1) {
    g = COLD_GRIP + (PEAK_GRIP - COLD_GRIP) * t.temp;
  } else {
    // Past the window grip falls away again, bottoming out at OVERHEAT_GRIP.
    g = PEAK_GRIP - (PEAK_GRIP - OVERHEAT_GRIP) * clamp(t.temp - 1, 0, 1);
  }
  return g - WEAR_GRIP_LOSS * t.wear;
}

export function tyreLabel(t: TyreState): string {
  if (t.temp < 0.45) return 'COLD';
  if (t.temp > 1.6) return 'OVERHEATING';
  if (t.wear > 0.6) return 'WORN';
  return 'OPTIMAL';
}
